import React, { useState, useEffect } from 'react';
import { connectAuthPost, setConnectDestination, normalizeUser } from './connectApi';
import ConnectSelect from './ConnectSelect';
import './styles/ConnectMeet.css';
import './styles/ConnectSetup.css';

/**
 * Where you are, what you are into, and when you are around.
 *
 * This is deliberately not part of sign-up. Accounts created before these
 * fields existed have no way back through onboarding, so the same panel is
 * reachable from the profile at any time and is how everyone — new or
 * existing — fills these in.
 *
 * It saves across four endpoints because they are genuinely separate things:
 * the profile, the tag selection, a trip, and a local's openness to meeting.
 */

const STATUSES = [
  { id: 'local', label: 'Local', hint: 'You live here' },
  { id: 'traveler', label: 'Traveller', hint: 'You are visiting' },
  { id: 'returning', label: 'Returning visitor', hint: 'You come back often, or you are from here' },
];

const STAY_TYPES = [
  { id: 'day', label: 'Day visit' },
  { id: 'overnight', label: 'Overnight' },
  { id: 'long', label: 'Long stay (14+ nights)' },
];

const PARTIES = [
  { id: 'solo', label: 'Solo' },
  { id: 'partner', label: 'With a partner' },
  { id: 'friends', label: 'With friends' },
];

export default function ConnectSetup({ user, onClose, onSaved }) {
  const [destinations, setDestinations] = useState([]);
  const [tags, setTags] = useState([]);

  const [destinationId, setDestinationId] = useState(user?.destinationId || '');
  const [status, setStatus] = useState(user?.status || 'traveler');
  const [selectedTags, setSelectedTags] = useState(user?.tagIds || []);
  const [openToMeet, setOpenToMeet] = useState(!!user?.openToMeet);

  const [trip, setTrip] = useState({
    arrival: '',
    departure: '',
    stay_type: 'overnight',
    party: 'solo',
    show_exact_dates: true,
  });
  const [hasTrip, setHasTrip] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isTraveller = status === 'traveler' || status === 'returning';

  useEffect(() => {
    const load = async () => {
      try {
        const [destRes, tagRes, presenceRes] = await Promise.all([
          connectAuthPost('connect/destinations/list'),
          connectAuthPost('connect/tags/list'),
          connectAuthPost('connect/presence/get'),
        ]);

        if (destRes.success) setDestinations(destRes.data.destinations);
        if (tagRes.success) setTags(tagRes.data.tags);

        if (presenceRes.success) {
          const p = presenceRes.data;
          if (p.destination_id) setDestinationId(p.destination_id);
          if (p.status) setStatus(p.status);
          setOpenToMeet(!!p.open_to_meet);
          if (p.trip) {
            setHasTrip(true);
            setTrip({
              arrival: p.trip.arrival || '',
              departure: p.trip.departure || '',
              stay_type: p.trip.stay_type || 'overnight',
              party: p.trip.party || 'solo',
              show_exact_dates: p.trip.show_exact_dates !== false,
            });
          }
        }
      } catch (err) {
        setError('Could not load your details.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const toggleTag = (id) => {
    setSelectedTags((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setError('');
    if (!destinationId) { setError('Choose where you are.'); return; }

    if (isTraveller && hasTrip) {
      if (!trip.arrival || !trip.departure) { setError('Add both trip dates, or turn the trip off.'); return; }
      if (trip.departure < trip.arrival) { setError('Departure cannot be before arrival.'); return; }
    }

    setIsSaving(true);
    try {
      // Where you are and how you relate to it.
      const profileRes = await connectAuthPost('connect/profile/update', {
        destination_id: parseInt(destinationId, 10),
        status,
      });
      if (!profileRes.success) {
        setError(profileRes.response || 'Could not save your profile.');
        return;
      }

      await connectAuthPost('connect/tags/set', { tag_ids: selectedTags });

      if (isTraveller && hasTrip) {
        const tripRes = await connectAuthPost('connect/trips/save', {
          destination_id: parseInt(destinationId, 10),
          arrival: trip.arrival,
          departure: trip.departure,
          stay_type: trip.stay_type,
          party: trip.party,
          show_exact_dates: trip.show_exact_dates,
          open_to_connect: true,
        });
        if (!tripRes.success) {
          setError(tripRes.response || 'Could not save your trip.');
          return;
        }
      }

      if (status === 'local') {
        await connectAuthPost('connect/presence/open-to-meet', {
          open_to_meet: openToMeet,
          destination_id: parseInt(destinationId, 10),
        });
      }

      setConnectDestination(parseInt(destinationId, 10));
      onSaved(normalizeUser(profileRes.data.user));
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsSaving(false);
    }
  };

  const activityTags = tags.filter((t) => t.type === 'activity');
  const styleTags = tags.filter((t) => t.type === 'style');
  const intentionTags = tags.filter((t) => t.type === 'intention');
  const categories = [...new Set(activityTags.map((t) => t.category))];

  return (
    <div className="meet-overlay" onClick={onClose}>
      <div className="meet-modal setup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meet-modal-head">
          <h2>Where you are and what you are into</h2>
          <button className="meet-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="meet-modal-body">
          {isLoading ? (
            <p>Loading…</p>
          ) : (
            <>
              <label className="meet-label">Destination</label>
              <ConnectSelect
                ariaLabel="Destination"
                value={destinationId}
                placeholder="Choose a destination"
                options={destinations.map((d) => ({ value: d.id, label: d.name }))}
                onChange={setDestinationId}
              />

              <label className="meet-label">You are</label>
              <div className="setup-options">
                {STATUSES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`setup-option ${status === s.id ? 'active' : ''}`}
                    onClick={() => setStatus(s.id)}
                  >
                    <strong>{s.label}</strong>
                    <span>{s.hint}</span>
                  </button>
                ))}
              </div>

              {/* Locals volunteer instead of setting dates */}
              {status === 'local' && (
                <label className="meet-check setup-toggle">
                  <input
                    type="checkbox"
                    checked={openToMeet}
                    onChange={(e) => setOpenToMeet(e.target.checked)}
                  />
                  I am open to meeting visitors
                </label>
              )}

              {/* Travellers say when they are around */}
              {isTraveller && (
                <>
                  <label className="meet-check setup-toggle">
                    <input
                      type="checkbox"
                      checked={hasTrip}
                      onChange={(e) => setHasTrip(e.target.checked)}
                    />
                    I am here for a set period
                  </label>

                  {hasTrip && (
                    <div className="setup-trip">
                      <div className="meet-row">
                        <div>
                          <label className="meet-label">Arrive</label>
                          <input type="date" className="meet-input" value={trip.arrival}
                                 onChange={(e) => setTrip({ ...trip, arrival: e.target.value })} />
                        </div>
                        <div>
                          <label className="meet-label">Leave</label>
                          <input type="date" className="meet-input" value={trip.departure}
                                 onChange={(e) => setTrip({ ...trip, departure: e.target.value })} />
                        </div>
                      </div>

                      <div className="meet-row">
                        <div>
                          <label className="meet-label">Stay</label>
                          <ConnectSelect
                            ariaLabel="Stay type"
                            value={trip.stay_type}
                            options={STAY_TYPES.map((s) => ({ value: s.id, label: s.label }))}
                            onChange={(v) => setTrip({ ...trip, stay_type: v })}
                          />
                        </div>
                        <div>
                          <label className="meet-label">Travelling</label>
                          <ConnectSelect
                            ariaLabel="Who you are travelling with"
                            value={trip.party}
                            options={PARTIES.map((s) => ({ value: s.id, label: s.label }))}
                            onChange={(v) => setTrip({ ...trip, party: v })}
                          />
                        </div>
                      </div>

                      <label className="meet-check">
                        <input
                          type="checkbox"
                          checked={trip.show_exact_dates}
                          onChange={(e) => setTrip({ ...trip, show_exact_dates: e.target.checked })}
                        />
                        Show my exact dates
                      </label>
                      {!trip.show_exact_dates && (
                        <p className="setup-hint">
                          Others will see something vaguer, like “Here this week”.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              <label className="meet-label">
                What you are into
                <span className="meet-hint"> this is what matches you with people</span>
              </label>

              {categories.map((cat) => (
                <div key={cat} className="setup-group">
                  <span className="setup-group-name">{cat}</span>
                  <div className="setup-tags">
                    {activityTags.filter((t) => t.category === cat).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`meet-chip ${selectedTags.includes(t.id) ? 'active' : ''}`}
                        onClick={() => toggleTag(t.id)}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="setup-group">
                <span className="setup-group-name">How you travel</span>
                <div className="setup-tags">
                  {styleTags.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`meet-chip ${selectedTags.includes(t.id) ? 'active' : ''}`}
                      onClick={() => toggleTag(t.id)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="setup-group">
                <span className="setup-group-name">What you are here for</span>
                <div className="setup-tags">
                  {intentionTags.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`meet-chip ${selectedTags.includes(t.id) ? 'active' : ''}`}
                      onClick={() => toggleTag(t.id)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              <p className="setup-hint">
                {selectedTags.length === 0
                  ? 'Pick a few — with none, nobody can match with you.'
                  : `${selectedTags.length} selected`}
              </p>

              {error && <div className="meet-error">{error}</div>}
            </>
          )}
        </div>

        <div className="meet-modal-foot">
          <button className="meet-btn ghost" onClick={onClose}>Cancel</button>
          <button className="meet-btn primary" onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
